import type { CompetitorRecord } from "@/lib/compare/types";

const PRICING = "https://syllaby.io/pricing";
const TERMS = "https://syllaby.io/terms-and-conditions/";
const CHECKED = "2026-10-08";

export const SYLLABY: CompetitorRecord = {
  slug: "syllaby",
  name: "Syllaby",
  aliases: ["Syllaby.io", "Syllaby AI", "syllaby io"],
  url: "https://syllaby.io",
  pricingUrl: PRICING,
  category: "faceless",
  positioning: "Turn Any Idea Into Faceless Videos & AI Avatars, with idea discovery, scheduling and analytics in one tool.",
  plans: [
    {
      name: "Starter",
      monthly: 9,
      metered: "100 credits per month, 13 credits per faceless video minute",
      includes: [
        "Monthly billing only, no annual option",
        "About 7 faceless videos a month by Syllaby's own math",
        "No voice clone",
        "1 GB storage",
        "Full-resolution export, unlimited video length",
      ],
      sourceUrl: PRICING,
      checkedAt: CHECKED,
      note: "The pricing page lists Starter as monthly only.",
    },
    {
      name: "Basic",
      monthly: 29,
      annualPerMonth: 24.67,
      annualTotal: 296,
      metered: "500 credits per month, 13 credits per faceless video minute",
      includes: [
        "About 38 faceless videos a month by Syllaby's own math",
        "1 voice clone",
        "5 GB storage",
        "Content calendar and bulk scheduler",
        "Full-resolution export, unlimited video length",
      ],
      sourceUrl: PRICING,
      checkedAt: CHECKED,
    },
    {
      name: "Standard",
      monthly: 78,
      annualPerMonth: 66.33,
      annualTotal: 796,
      metered: "1,500 credits per month, 13 credits per faceless video minute",
      includes: [
        "About 115 faceless videos a month by Syllaby's own math",
        "3 voice clones",
        "10 GB storage",
        "Full-resolution export, unlimited video length",
      ],
      sourceUrl: PRICING,
      checkedAt: CHECKED,
    },
    {
      name: "Premium",
      monthly: 153,
      annualPerMonth: 130.08,
      annualTotal: 1561,
      metered: "5,000 credits per month, 13 credits per faceless video minute",
      includes: [
        "About 384 faceless videos a month by Syllaby's own math",
        "5 voice clones",
        "20 GB storage",
        "Full-resolution export, unlimited video length",
      ],
      sourceUrl: PRICING,
      checkedAt: CHECKED,
    },
    {
      name: "Enterprise",
      custom: true,
      metered: "Custom credits",
      includes: ["Custom clones and storage", "API access by talking to their team"],
      sourceUrl: PRICING,
      checkedAt: CHECKED,
    },
  ],
  entryPlanName: "Starter",
  entryPlanIncludes: "100 credits, about 7 faceless videos, no voice clone, 1 GB storage, monthly billing only",
  freeTier: "No free plan. The homepage offers a free first video plus 50 credits at signup.",
  trial: "Contradictory: the pricing page title says Free Trial Included and blog CTAs say Try Free for 7 Days, while the pricing FAQ says there is no free trial.",
  meteringUnit: "13 credits per minute of faceless video, 50 per minute of avatar video, 15 per discovered idea, billed by the whole minute",
  exportResolution: "Full-resolution export on every plan; no numeric resolution published",
  features: {
    longToShort: { v: "no", note: "Script-to-video only, no source clipping" },
    momentRanking: { v: "no" },
    reframing: { v: "no", note: "One-click resize in the editor only" },
    maxSource: { v: "n/a", note: "Generates from scripts, no source upload to clip" },
    layouts: { v: "n/a", note: "Not enumerated on the pricing page" },
    redditStory: { v: "partial", note: "Script-to-video, no Reddit importer" },
    fakeText: { v: "no", note: "Not on the features page" },
    aiImages: { v: "yes", note: "18 styles plus Veo, Seedance, Kling scenes" },
    voiceover: { v: "yes", note: "Preset voices, engine not named" },
    voiceCloning: { v: "yes", note: "1, 3 or 5 clones by plan, none on Starter" },
    avatars: { v: "yes", note: "Avatars 2.0 and Real Clone twins" },
    timelineEditor: { v: "partial", note: "Trim, crop, filters, transitions; no timeline described" },
    captionStyles: { v: "n/a", note: "No style count published" },
    captionLanguages: { v: "n/a", note: "Says multiple languages, no count" },
    bRoll: { v: "partial", note: "Stock footage and generated scenes" },
    fourK: { v: "n/a", note: "Says full-resolution, no number" },
    scheduling: { v: "yes", note: "Content Calendar and Bulk Scheduler" },
    analytics: { v: "yes", note: "Social Analytics for connected accounts" },
    socialTracker: { v: "no" },
    api: { v: "partial", note: "Enterprise-gated, talk to their team" },
    webhooks: { v: "n/a", note: "Not on the API page as fetched" },
    web: { v: "yes" },
    desktop: { v: "no", note: "Not advertised" },
    mobile: { v: "n/a", note: "Not stated on the pricing page" },
    teamSeats: { v: "n/a", note: "Not stated on the pricing page" },
    freeTier: { v: "partial", note: "Free first video and 50 signup credits" },
    trial: { v: "text", note: "Pages conflict: 7 days vs none" },
    metering: { v: "text", note: "13 credits per faceless minute, whole minutes" },
    rollover: { v: "no", note: "Credits do not roll over" },
    refunds: { v: "text", note: "FAQ says 30 days; Terms say non-refundable" },
    cancelInApp: { v: "n/a", note: "Not described on the pricing page" },
  },
  billingTerms: {
    refunds:
      "The pricing FAQ says refund requests are accepted within 30 days of payment. Section 16 of the Terms says paid subscription fees are non-refundable except case by case, with credits refunded only for a verified technical error.",
    cancel:
      "Cancellation steps are not described on the pricing page. The Terms say a trial converts and the card is charged on the last day of the trial unless you cancelled.",
    sourceUrl: TERMS,
    checkedAt: CHECKED,
  },
  quotes: [
    {
      text: "I can't even cancel it through.",
      date: "2026-08-27",
      url: "https://www.trustpilot.com/review/syllaby.io?page=2",
      evidences: "cancelInApp",
    },
  ],
  verification: {
    method: "fetch",
    date: CHECKED,
    note: "Pricing page and Terms fetched directly on Oct 8, 2026. Monthly prices, annual per-month and annual totals for Basic ($24.67, $296), Standard ($66.33, $796) and Premium ($130.08, $1,561), the credit allowances and the 13-credits-per-minute rule all come from syllaby.io/pricing. The trial contradiction is between the pricing page title, the pricing FAQ and the homepage. Third-party only, not used as fact: a card-required 7-day trial and scheduling caps per plan from aifunnelinsider.com.",
  },
  seoPattern: "syllaby.io/blog/<competitor>-alternative-<differentiator>, no page about Crayo",
};
